<script>
	import { onMount } from 'svelte';
	import { superAdminAPI, aiProvidersAPI } from './api.js';
	import { toast } from './lib/toast.js';
	import Icon from './lib/Icon.svelte';
	import SuperAdminOrgDetail from './SuperAdminOrgDetail.svelte';

	let activeTab = $state('overview'); // overview | orgs | packages | billing | users | audit
	let loading = $state(true);

	// Overview
	let dashboard = $state(null);

	// Packages & modules
	let packages = $state([]);
	let modules = $state([]);
	let editingPackage = $state(null);   // null | {} (create) | package object (edit)
	let packageForm = $state(null);

	// AI billing
	let billing = $state(null);          // overview payload
	let billingDays = $state(30);
	let billingSettings = $state(null);
	let platformProviders = $state([]);
	let modelsCatalog = $state({});
	let topupOrg = $state(null);         // org row for the Recargar modal
	let topupForm = $state({ amount_usd: '', amount_crc: '', description: '' });
	let purchaseRequests = $state([]);   // customer package/top-up requests
	let editingProvider = $state(null);  // null | {} | provider
	let providerForm = $state(null);

	// Orgs
	let orgs = $state([]);
	let orgFilter = $state({ q: '', status: '', plan: '' });
	let editingOrgId = $state(null);
	let creatingOrg = $state(false);
	let createForm = $state({ name: '', plan: 'starter', admin_email: '', admin_password: '', admin_name: '' });

	// Users
	let users = $state([]);
	let userQuery = $state('');

	// Audit
	let audit = $state({ items: [], page: 1, total: 0, per_page: 30 });
	let auditFilter = $state({ action: '' });

	// Derived chart data
	let chartSeries = $derived(dashboard?.series?.conversations || []);
	let chartAiSeries = $derived(dashboard?.series?.ai_interactions || []);
	let chartMax = $derived(
		Math.max(1, ...chartSeries.map(s => s.value), ...chartAiSeries.map(s => s.value))
	);
	let chartStepX = $derived(chartSeries.length > 1 ? (568 - 32) / (chartSeries.length - 1) : 0);
	let chartConvsPath = $derived(
		chartSeries.map((p, i) => `${32 + i * chartStepX},${150 - (p.value / chartMax) * 120}`).join(' ')
	);
	let chartAiPath = $derived(
		chartAiSeries.map((p, i) => `${32 + i * chartStepX},${150 - (p.value / chartMax) * 120}`).join(' ')
	);

	async function loadOverview() {
		try {
			dashboard = await superAdminAPI.dashboard();
		} catch (e) { toast.error(e.message || 'Error cargando dashboard'); }
	}

	async function loadOrgs() {
		try {
			orgs = await superAdminAPI.listOrgs(orgFilter.q, orgFilter.status, orgFilter.plan);
		} catch (e) { toast.error(e.message || 'Error cargando organizaciones'); }
	}

	async function loadUsers() {
		try {
			users = await superAdminAPI.listUsers(userQuery);
		} catch (e) { toast.error(e.message || 'Error cargando usuarios'); }
	}

	async function loadPackages() {
		try {
			const [pkgs, mods] = await Promise.all([
				superAdminAPI.listPackages(),
				modules.length ? Promise.resolve(modules) : superAdminAPI.listModules()
			]);
			packages = pkgs;
			modules = mods;
		} catch (e) { toast.error(e.message || 'Error cargando paquetes'); }
	}

	async function loadBilling() {
		try {
			const [overview, settings, providers, requests] = await Promise.all([
				superAdminAPI.billingOverview(billingDays),
				superAdminAPI.billingSettings(),
				superAdminAPI.listPlatformProviders(),
				superAdminAPI.listPurchaseRequests()
			]);
			billing = overview;
			billingSettings = settings;
			platformProviders = providers;
			purchaseRequests = requests;
			if (!Object.keys(modelsCatalog).length) {
				try { modelsCatalog = await aiProvidersAPI.getModels(); } catch {}
			}
		} catch (e) { toast.error(e.message || 'Error cargando facturación'); }
	}

	function fmtUsd(v) {
		return `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
	}
	function fmtCrc(v) {
		return `₡${Math.round(Number(v) || 0).toLocaleString('es-CR')}`;
	}

	function openTopup(orgRow) {
		topupOrg = orgRow;
		topupForm = { amount_usd: '', amount_crc: '', description: '' };
	}
	function syncTopupFromCrc() {
		const rate = billing?.exchange_rate_crc || 460;
		const crc = Number(topupForm.amount_crc);
		if (crc > 0) topupForm.amount_usd = (crc / rate).toFixed(2);
	}
	function syncTopupFromUsd() {
		const rate = billing?.exchange_rate_crc || 460;
		const usd = Number(topupForm.amount_usd);
		if (usd > 0) topupForm.amount_crc = Math.round(usd * rate);
	}
	async function submitTopup(e) {
		e.preventDefault();
		try {
			await superAdminAPI.creditOrg(topupOrg.id, {
				amount_usd: Number(topupForm.amount_usd),
				description: topupForm.description
			});
			toast.success(`Recarga de ${fmtUsd(topupForm.amount_usd)} aplicada a ${topupOrg.name}`);
			topupOrg = null;
			loadBilling();
		} catch (e2) { toast.error(e2.message); }
	}

	async function saveBillingSettings() {
		try {
			billingSettings = await superAdminAPI.saveBillingSettings({
				markup_percent: Number(billingSettings.markup_percent),
				exchange_rate_crc: Number(billingSettings.exchange_rate_crc),
				low_balance_threshold_usd: Number(billingSettings.low_balance_threshold_usd),
				min_topup_crc: Number(billingSettings.min_topup_crc),
				sinpe_number: billingSettings.sinpe_number,
				payment_whatsapp: billingSettings.payment_whatsapp
			});
			toast.success('Configuración guardada');
			loadBilling();
		} catch (e) { toast.error(e.message); }
	}

	async function approveRequest(r) {
		const what = r.kind === 'topup'
			? `recarga de ${fmtCrc(r.amount_crc)}`
			: `paquete "${r.package_name}"${r.amount_crc ? ` (${fmtCrc(r.amount_crc)})` : ''}`;
		if (!confirm(`¿Aprobar la ${what} para ${r.organization_name}?\n(Verifica primero el comprobante SINPE recibido por WhatsApp)`)) return;
		try {
			await superAdminAPI.approvePurchaseRequest(r.id);
			toast.success('Solicitud aprobada y aplicada');
			loadBilling();
		} catch (e) { toast.error(e.message); }
	}

	async function rejectRequest(r) {
		const note = prompt(`Motivo del rechazo para ${r.organization_name} (lo verá el cliente):`, 'No se recibió el comprobante de pago');
		if (note === null) return;
		try {
			await superAdminAPI.rejectPurchaseRequest(r.id, note);
			toast.warning('Solicitud rechazada');
			loadBilling();
		} catch (e) { toast.error(e.message); }
	}

	function openProviderForm(p = null) {
		editingProvider = p;
		providerForm = p ? {
			name: p.name, provider_type: p.provider_type, model_name: p.model_name || '',
			api_key: '', is_active: p.is_active
		} : {
			name: 'Mangu AI', provider_type: 'anthropic', model_name: '', api_key: '', is_active: true
		};
	}
	async function submitProvider(e) {
		e.preventDefault();
		try {
			const payload = { ...providerForm };
			if (!payload.api_key) delete payload.api_key;  // keep existing key on edit
			if (editingProvider?.id) {
				await superAdminAPI.updatePlatformProvider(editingProvider.id, payload);
				toast.success('Proveedor actualizado');
			} else {
				await superAdminAPI.createPlatformProvider(payload);
				toast.success('Proveedor de plataforma creado');
			}
			editingProvider = null;
			providerForm = null;
			loadBilling();
		} catch (e2) { toast.error(e2.message); }
	}
	async function removeProvider(p) {
		if (!confirm(`¿Eliminar el proveedor "${p.name}"?`)) return;
		try {
			await superAdminAPI.deletePlatformProvider(p.id);
			toast.success('Proveedor eliminado');
			loadBilling();
		} catch (e) { toast.error(e.message); }
	}

	let billingChartMax = $derived(Math.max(0.0001, ...(billing?.series || []).map(s => s.cost_usd)));
	let billingStepX = $derived((billing?.series?.length || 0) > 1 ? (568 - 32) / (billing.series.length - 1) : 0);
	let billingPath = $derived(
		(billing?.series || []).map((p, i) => `${32 + i * billingStepX},${150 - (p.cost_usd / billingChartMax) * 120}`).join(' ')
	);

	const LIMIT_LABELS = {
		max_agents: 'Usuarios',
		max_channels: 'Canales',
		max_ai_agents: 'AI Agents',
		max_conversations_per_month: 'Conv./mes',
		max_knowledge_entries: 'Conocimiento',
		max_flows: 'Flujos'
	};

	function openPackageForm(pkg = null) {
		editingPackage = pkg;
		packageForm = pkg ? {
			name: pkg.name,
			description: pkg.description || '',
			price_usd: pkg.price_usd ?? 0,
			price_crc: pkg.price_crc ?? 0,
			is_active: pkg.is_active,
			feature_flags: { ...(pkg.feature_flags || {}) },
			...Object.fromEntries(Object.keys(LIMIT_LABELS).map(k => [k, pkg[k]]))
		} : {
			name: '', description: '', price_usd: 0, price_crc: 0, is_active: true,
			feature_flags: Object.fromEntries(modules.map(m => [m.key, false])),
			max_agents: 2, max_channels: 1, max_ai_agents: 2,
			max_conversations_per_month: 500, max_knowledge_entries: 50, max_flows: 3
		};
	}

	async function submitPackage(e) {
		e.preventDefault();
		try {
			if (editingPackage?.id) {
				await superAdminAPI.updatePackage(editingPackage.id, packageForm);
				toast.success('Paquete actualizado');
			} else {
				await superAdminAPI.createPackage(packageForm);
				toast.success('Paquete creado');
			}
			editingPackage = null;
			packageForm = null;
			loadPackages();
		} catch (e2) { toast.error(e2.message); }
	}

	async function removePackage(pkg) {
		if (!confirm(`¿Eliminar el paquete "${pkg.name}"?`)) return;
		try {
			await superAdminAPI.deletePackage(pkg.id);
			toast.success('Paquete eliminado');
			loadPackages();
		} catch (e) { toast.error(e.message); }
	}

	async function togglePackageActive(pkg) {
		try {
			await superAdminAPI.updatePackage(pkg.id, { is_active: !pkg.is_active });
			toast.success(pkg.is_active ? 'Paquete desactivado' : 'Paquete activado');
			loadPackages();
		} catch (e) { toast.error(e.message); }
	}

	function enabledModules(pkg) {
		const flags = pkg.feature_flags || {};
		return modules.filter(m => flags[m.key]);
	}

	function fmtLimit(v) {
		return (v === null || v === undefined || v < 0) ? '∞' : v.toLocaleString();
	}

	async function loadAudit(page = 1) {
		try {
			audit = await superAdminAPI.audit(page, auditFilter);
		} catch (e) { toast.error(e.message || 'Error cargando audit'); }
	}

	async function setTab(t) {
		activeTab = t;
		loading = true;
		try {
			if (t === 'overview') await loadOverview();
			else if (t === 'orgs') await loadOrgs();
			else if (t === 'packages') await loadPackages();
			else if (t === 'billing') await loadBilling();
			else if (t === 'users') await loadUsers();
			else if (t === 'audit') await loadAudit(1);
		} finally { loading = false; }
	}

	async function suspendOrg(o) {
		const reason = prompt(`Motivo de suspensión de "${o.name}":`, 'Pago pendiente');
		if (!reason) return;
		try {
			await superAdminAPI.suspendOrg(o.id, reason);
			toast.warning(`${o.name} suspendida`);
			loadOrgs();
		} catch (e) { toast.error(e.message); }
	}

	async function resumeOrg(o) {
		try {
			await superAdminAPI.resumeOrg(o.id);
			toast.success(`${o.name} reactivada`);
			loadOrgs();
		} catch (e) { toast.error(e.message); }
	}

	async function deleteOrg(o) {
		if (!confirm(`¿ELIMINAR PERMANENTEMENTE "${o.name}"? Esto borrará todos sus datos. No se puede deshacer.`)) return;
		const confirm2 = prompt(`Para confirmar, escribe el nombre exacto:`);
		if (confirm2 !== o.name) { toast.warning('Cancelado'); return; }
		try {
			await superAdminAPI.deleteOrg(o.id);
			toast.success(`${o.name} eliminada`);
			loadOrgs();
		} catch (e) { toast.error(e.message); }
	}

	async function impersonate(o) {
		if (!confirm(`Iniciar sesión como administrador de "${o.name}"?\n(Tu sesión actual será reemplazada por 30 minutos)`)) return;
		try {
			const res = await superAdminAPI.impersonate(o.id);
			localStorage.setItem('access_token', res.access_token);
			localStorage.setItem('user', JSON.stringify(res.user));
			localStorage.setItem('impersonating', JSON.stringify({
				org_name: o.name, expires_at: Date.now() + (res.expires_in * 1000)
			}));
			toast.info('Impersonando — recargando...');
			setTimeout(() => window.location.reload(), 800);
		} catch (e) { toast.error(e.message); }
	}

	async function applyPlan(o, plan) {
		try {
			await superAdminAPI.applyPlan(o.id, plan);
			toast.success(`Plan ${plan} aplicado a ${o.name}`);
			loadOrgs();
		} catch (e) { toast.error(e.message); }
	}

	async function submitCreate(e) {
		e.preventDefault();
		try {
			await superAdminAPI.createOrg({
				name: createForm.name, plan: createForm.plan,
				admin_email: createForm.admin_email || null,
				admin_password: createForm.admin_password || null,
				admin_name: createForm.admin_name || null
			});
			toast.success('Organización creada');
			creatingOrg = false;
			createForm = { name: '', plan: 'starter', admin_email: '', admin_password: '', admin_name: '' };
			loadOrgs();
		} catch (e2) { toast.error(e2.message); }
	}

	async function changeUserRole(u, role) {
		try {
			await superAdminAPI.updateUser(u.id, { role });
			toast.success(`Rol actualizado`);
			loadUsers();
		} catch (e) { toast.error(e.message); }
	}

	async function resetUserPw(u) {
		const pw = prompt(`Nueva contraseña para ${u.email}:`);
		if (!pw) return;
		try {
			await superAdminAPI.resetPassword(u.id, pw);
			toast.success('Contraseña reiniciada');
		} catch (e) { toast.error(e.message); }
	}

	async function toggleUserActive(u) {
		try {
			await superAdminAPI.updateUser(u.id, { is_active: !u.is_active });
			toast.success(u.is_active ? 'Usuario desactivado' : 'Usuario activado');
			loadUsers();
		} catch (e) { toast.error(e.message); }
	}

	function fmtDate(iso) {
		if (!iso) return '—';
		try { return new Date(iso).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' }); }
		catch { return iso; }
	}
	function fmtDateTime(iso) {
		if (!iso) return '—';
		try {
			const d = new Date(iso);
			return d.toLocaleString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
		} catch { return iso; }
	}

	const PLAN_BADGE = {
		free: 'badge', starter: 'badge-info', pro: 'badge-accent', enterprise: 'badge-success'
	};

	onMount(() => setTab('overview'));
</script>

{#if editingOrgId !== null}
	<SuperAdminOrgDetail
		orgId={editingOrgId}
		onClose={() => { editingOrgId = null; loadOrgs(); }}
	/>
{:else}
<div class="sa-view">
	<div class="sa-tabs">
		<button class:active={activeTab === 'overview'} onclick={() => setTab('overview')}>
			<Icon name="pie-chart" size={14} /> Vista global
		</button>
		<button class:active={activeTab === 'orgs'} onclick={() => setTab('orgs')}>
			<Icon name="building" size={14} /> Organizaciones
		</button>
		<button class:active={activeTab === 'packages'} onclick={() => setTab('packages')}>
			<Icon name="box" size={14} /> Paquetes
		</button>
		<button class:active={activeTab === 'billing'} onclick={() => setTab('billing')}>
			<Icon name="sparkles" size={14} /> IA & Créditos
		</button>
		<button class:active={activeTab === 'users'} onclick={() => setTab('users')}>
			<Icon name="users" size={14} /> Usuarios
		</button>
		<button class:active={activeTab === 'audit'} onclick={() => setTab('audit')}>
			<Icon name="list" size={14} /> Auditoría
		</button>
	</div>

	<div class="sa-body">
		{#if loading}
			<div class="loading"><div class="spinner"></div></div>

		{:else if activeTab === 'overview'}
			{#if !dashboard}
				<div class="empty">Sin datos</div>
			{:else}
				<div class="kpis">
					<div class="kpi card"><div class="kpi-num">{dashboard.totals.organizations}</div><div class="kpi-lbl">Organizaciones</div><div class="kpi-sub">+{dashboard.totals.new_orgs_30d} en 30d</div></div>
					<div class="kpi card"><div class="kpi-num">{dashboard.totals.active}</div><div class="kpi-lbl">Activas</div><div class="kpi-sub muted">{dashboard.totals.suspended} suspendidas</div></div>
					<div class="kpi card"><div class="kpi-num">{dashboard.totals.users}</div><div class="kpi-lbl">Usuarios totales</div></div>
					<div class="kpi card"><div class="kpi-num">{dashboard.totals.conversations.toLocaleString()}</div><div class="kpi-lbl">Conversaciones</div><div class="kpi-sub">+{dashboard.totals.conversations_30d.toLocaleString()} en 30d</div></div>
					<div class="kpi card"><div class="kpi-num">{dashboard.totals.ai_interactions.toLocaleString()}</div><div class="kpi-lbl">Mensajes IA</div><div class="kpi-sub">+{dashboard.totals.ai_interactions_30d.toLocaleString()} en 30d</div></div>
				</div>

				<div class="charts">
					<div class="card chart-card">
						<h3>Actividad últimos 14 días</h3>
						<svg viewBox="0 0 600 180" class="chart">
							{#each [0.25, 0.5, 0.75, 1] as p}
								<line x1="32" x2="568" y1={150 - p * 120} y2={150 - p * 120} stroke="var(--border-1)" stroke-dasharray="2 4" />
							{/each}
							<polyline fill="none" stroke="var(--accent)" stroke-width="2" points={chartConvsPath} />
							<polyline fill="none" stroke="var(--brand)" stroke-width="2" stroke-dasharray="3 3" points={chartAiPath} />
						</svg>
						<div class="legend">
							<span><span class="dot" style="background:var(--accent)"></span> Conversaciones</span>
							<span><span class="dot" style="background:var(--brand)"></span> Mensajes IA</span>
						</div>
					</div>

					<div class="card plans-card">
						<h3>Distribución por plan</h3>
						<ul class="plan-dist">
							{#each Object.entries(dashboard.plan_distribution) as [plan, count]}
								<li>
									<span class="badge {PLAN_BADGE[plan] || 'badge'}">{plan}</span>
									<span class="dist-count">{count}</span>
									<div class="dist-bar">
										<div class="dist-fill" style="width:{(count / dashboard.totals.organizations) * 100}%"></div>
									</div>
								</li>
							{/each}
						</ul>
					</div>
				</div>
			{/if}

		{:else if activeTab === 'orgs'}
			<div class="filters">
				<div class="search-input">
					<Icon name="search" size={14} class="si" />
					<input type="text" placeholder="Buscar organización..." bind:value={orgFilter.q} oninput={() => loadOrgs()} />
				</div>
				<select bind:value={orgFilter.status} onchange={() => loadOrgs()}>
					<option value="">Todos los estados</option>
					<option value="active">Activas</option>
					<option value="suspended">Suspendidas</option>
				</select>
				<select bind:value={orgFilter.plan} onchange={() => loadOrgs()}>
					<option value="">Todos los planes</option>
					<option value="free">Free</option>
					<option value="starter">Starter</option>
					<option value="pro">Pro</option>
					<option value="enterprise">Enterprise</option>
				</select>
				<button class="btn btn-primary btn-sm" onclick={() => (creatingOrg = true)}>
					<Icon name="plus" size={14} /> Nueva organización
				</button>
			</div>

			<table class="data-table">
				<thead>
					<tr>
						<th>Organización</th>
						<th>Plan</th>
						<th>Usuarios</th>
						<th>Conv. 30d</th>
						<th>AI Agents</th>
						<th>Estado</th>
						<th>Creada</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					{#each orgs as o}
						<tr>
							<td>
								<button class="org-link" onclick={() => (editingOrgId = o.id)}>
									<div class="org-mark" style="background:linear-gradient(135deg, var(--brand), var(--brand-dark))">{o.name?.charAt(0)}</div>
									<div>
										<div class="org-name">{o.name}</div>
										<div class="org-slug">{o.slug}</div>
									</div>
								</button>
							</td>
							<td>
								<span class="badge {PLAN_BADGE[o.plan] || 'badge'}">{o.plan}</span>
							</td>
							<td>{o.users_count ?? 0}</td>
							<td>{o.conversations_30d ?? 0}</td>
							<td>{o.ai_agents_count ?? 0}</td>
							<td>
								{#if o.is_suspended}
									<span class="badge badge-danger" title={o.suspended_reason}>Suspendida</span>
								{:else}
									<span class="badge badge-success">Activa</span>
								{/if}
							</td>
							<td class="muted">{fmtDate(o.created_at)}</td>
							<td class="actions">
								<button class="icon-btn" title="Configurar" onclick={() => (editingOrgId = o.id)}>
									<Icon name="settings" size={13} />
								</button>
								<button class="icon-btn" title="Iniciar sesión como" onclick={() => impersonate(o)}>
									<Icon name="user" size={13} />
								</button>
								{#if o.is_suspended}
									<button class="icon-btn success" title="Reactivar" onclick={() => resumeOrg(o)}>
										<Icon name="check" size={13} />
									</button>
								{:else}
									<button class="icon-btn warn" title="Suspender" onclick={() => suspendOrg(o)}>
										<Icon name="alert-circle" size={13} />
									</button>
								{/if}
								<button class="icon-btn danger" title="Eliminar" onclick={() => deleteOrg(o)}>
									<Icon name="trash" size={13} />
								</button>
							</td>
						</tr>
					{/each}
				</tbody>
			</table>
			{#if !orgs.length}<div class="empty">Sin organizaciones</div>{/if}

		{:else if activeTab === 'packages'}
			<div class="filters">
				<p class="muted" style="flex:1; font-size:12.5px;">
					Define paquetes con sus módulos y límites; luego asígnalos a cada organización desde su ficha.
				</p>
				<button class="btn btn-primary btn-sm" onclick={() => openPackageForm()}>
					<Icon name="plus" size={14} /> Nuevo paquete
				</button>
			</div>

			<table class="data-table">
				<thead>
					<tr>
						<th>Paquete</th>
						<th>Precio</th>
						<th>Módulos</th>
						<th>Límites</th>
						<th>Orgs</th>
						<th>Estado</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					{#each packages as p (p.id)}
						<tr class:pkg-inactive={!p.is_active}>
							<td>
								<button class="org-link" onclick={() => openPackageForm(p)}>
									<div class="org-mark" style="background:linear-gradient(135deg, var(--accent), var(--accent-hover))">
										<Icon name="box" size={15} />
									</div>
									<div>
										<div class="org-name">{p.name}</div>
										<div class="org-slug">{p.key}</div>
									</div>
								</button>
							</td>
							<td>
								<div>${p.price_usd ?? 0}/mes</div>
								{#if p.price_crc}<div class="muted-sm">₡{p.price_crc.toLocaleString('es-CR')}/mes</div>{/if}
							</td>
							<td>
								<div class="mod-chips">
									{#each enabledModules(p) as m}
										<span class="badge badge-accent" title={m.description}>{m.label}</span>
									{:else}
										<span class="muted-sm">Sin módulos</span>
									{/each}
								</div>
							</td>
							<td class="muted-sm">
								{#each Object.entries(LIMIT_LABELS) as [k, label]}
									<div>{label}: <b>{fmtLimit(p[k])}</b></div>
								{/each}
							</td>
							<td>{p.organizations_count ?? 0}</td>
							<td>
								{#if p.is_active}
									<span class="badge badge-success">Activo</span>
								{:else}
									<span class="badge">Inactivo</span>
								{/if}
							</td>
							<td class="actions">
								<button class="icon-btn" title="Editar" onclick={() => openPackageForm(p)}>
									<Icon name="edit" size={13} />
								</button>
								<button class="icon-btn warn" title={p.is_active ? 'Desactivar' : 'Activar'} onclick={() => togglePackageActive(p)}>
									<Icon name={p.is_active ? 'x' : 'check'} size={13} />
								</button>
								<button class="icon-btn danger" title="Eliminar" onclick={() => removePackage(p)}>
									<Icon name="trash" size={13} />
								</button>
							</td>
						</tr>
					{/each}
				</tbody>
			</table>
			{#if !packages.length}<div class="empty">Sin paquetes — crea el primero</div>{/if}

		{:else if activeTab === 'billing'}
			{#if !billing}
				<div class="empty">Sin datos</div>
			{:else}
				<div class="kpis">
					<div class="kpi card">
						<div class="kpi-num">{fmtUsd(billing.totals.consumed_usd)}</div>
						<div class="kpi-lbl">Consumido ({billing.days}d)</div>
						<div class="kpi-sub muted">{fmtCrc(billing.totals.consumed_usd * billing.exchange_rate_crc)}</div>
					</div>
					<div class="kpi card">
						<div class="kpi-num">{fmtUsd(billing.totals.revenue_usd)}</div>
						<div class="kpi-lbl">Ganancia por margen</div>
						<div class="kpi-sub">{fmtCrc(billing.totals.revenue_usd * billing.exchange_rate_crc)}</div>
					</div>
					<div class="kpi card">
						<div class="kpi-num">{fmtUsd(billing.totals.topups_usd)}</div>
						<div class="kpi-lbl">Recargas ({billing.days}d)</div>
					</div>
					<div class="kpi card">
						<div class="kpi-num" class:danger-num={billing.totals.low_balance_orgs > 0}>{billing.totals.low_balance_orgs}</div>
						<div class="kpi-lbl">Orgs con saldo bajo</div>
					</div>
					<div class="kpi card">
						<div class="kpi-num" class:danger-num={billing.totals.pending_requests > 0}>{billing.totals.pending_requests ?? 0}</div>
						<div class="kpi-lbl">Solicitudes pendientes</div>
					</div>
				</div>

				<div class="section-head"><h3>Solicitudes de clientes (SINPE)</h3></div>
				{#if purchaseRequests.length}
					<table class="data-table" style="margin-bottom: 18px;">
						<thead>
							<tr><th>Cuándo</th><th>Organización</th><th>Solicitud</th><th>Monto</th><th>Nota del cliente</th><th>Estado</th><th></th></tr>
						</thead>
						<tbody>
							{#each purchaseRequests as r (r.id)}
								<tr>
									<td class="muted">{fmtDateTime(r.created_at)}</td>
									<td><b>{r.organization_name}</b></td>
									<td>{r.kind === 'topup' ? 'Recarga IA' : `Paquete ${r.package_name || ''}`}</td>
									<td>
										{r.amount_crc ? fmtCrc(r.amount_crc) : '—'}
										{#if r.kind === 'topup' && r.amount_usd}<div class="muted-sm">{fmtUsd(r.amount_usd)}</div>{/if}
									</td>
									<td class="muted-sm">{r.note || '—'}</td>
									<td>
										{#if r.status === 'pending'}<span class="badge badge-warning">Pendiente</span>
										{:else if r.status === 'approved'}<span class="badge badge-success">Aprobada</span>
										{:else}<span class="badge">Rechazada</span>{/if}
									</td>
									<td class="actions">
										{#if r.status === 'pending'}
											<button class="btn btn-primary btn-sm" onclick={() => approveRequest(r)}>Aprobar</button>
											<button class="btn btn-secondary btn-sm" onclick={() => rejectRequest(r)}>Rechazar</button>
										{/if}
									</td>
								</tr>
							{/each}
						</tbody>
					</table>
				{:else}
					<div class="empty" style="margin-bottom: 18px;">Sin solicitudes — los clientes pueden pedir paquetes y recargas desde Configuración → Datos & Créditos.</div>
				{/if}

				<div class="charts">
					<div class="card chart-card">
						<div class="chart-head">
							<h3>Consumo de IA por día (USD)</h3>
							<div class="range-mini">
								{#each [7, 30, 90] as d}
									<button class:active={billingDays === d} onclick={() => { billingDays = d; loadBilling(); }}>{d}d</button>
								{/each}
							</div>
						</div>
						<svg viewBox="0 0 600 180" class="chart">
							{#each [0.25, 0.5, 0.75, 1] as p}
								<line x1="32" x2="568" y1={150 - p * 120} y2={150 - p * 120} stroke="var(--border-1)" stroke-dasharray="2 4" />
							{/each}
							<polyline fill="none" stroke="var(--accent)" stroke-width="2" points={billingPath} />
						</svg>
					</div>

					<div class="card plans-card">
						<h3>Configuración</h3>
						{#if billingSettings}
							<div class="settings-form">
								<label>Margen de ganancia (%)
									<input type="number" min="0" max="500" step="0.5" bind:value={billingSettings.markup_percent} />
								</label>
								<label>Tipo de cambio (₡/USD)
									<input type="number" min="1" step="1" bind:value={billingSettings.exchange_rate_crc} />
								</label>
								<label>Umbral saldo bajo (USD)
									<input type="number" min="0" step="0.5" bind:value={billingSettings.low_balance_threshold_usd} />
								</label>
								<label>Recarga mínima (₡)
									<input type="number" min="0" step="500" bind:value={billingSettings.min_topup_crc} />
								</label>
								<label>Número SINPE Móvil
									<input type="text" bind:value={billingSettings.sinpe_number} placeholder="8678-9960" />
								</label>
								<label>WhatsApp para comprobantes
									<input type="text" bind:value={billingSettings.payment_whatsapp} placeholder="50686789960" />
								</label>
								<button class="btn btn-primary btn-sm" onclick={saveBillingSettings}>Guardar</button>
							</div>
						{/if}
					</div>
				</div>

				<div class="section-head">
					<h3>Proveedores de plataforma (Mangu AI)</h3>
					<button class="btn btn-primary btn-sm" onclick={() => openProviderForm()}>
						<Icon name="plus" size={14} /> Nuevo proveedor
					</button>
				</div>
				<table class="data-table" style="margin-bottom: 18px;">
					<thead>
						<tr><th>Nombre</th><th>Proveedor</th><th>Modelo</th><th>API key</th><th>Estado</th><th></th></tr>
					</thead>
					<tbody>
						{#each platformProviders as p (p.id)}
							<tr>
								<td><b>{p.name}</b></td>
								<td>{p.provider_type}</td>
								<td class="muted">{p.model_name || '—'}</td>
								<td class="muted-sm">{p.api_key_masked || 'sin key'}</td>
								<td>
									{#if p.is_active}<span class="badge badge-success">Activo</span>
									{:else}<span class="badge">Inactivo</span>{/if}
								</td>
								<td class="actions">
									<button class="icon-btn" title="Editar" onclick={() => openProviderForm(p)}>
										<Icon name="edit" size={13} />
									</button>
									<button class="icon-btn danger" title="Eliminar" onclick={() => removeProvider(p)}>
										<Icon name="trash" size={13} />
									</button>
								</td>
							</tr>
						{/each}
					</tbody>
				</table>
				{#if !platformProviders.length}
					<div class="empty">Sin proveedores de plataforma — crea uno con tu API key para ofrecer "Mangu AI" a tus clientes.</div>
				{/if}

				<div class="section-head"><h3>Saldos por organización</h3></div>
				<table class="data-table" style="margin-bottom: 18px;">
					<thead>
						<tr><th>Organización</th><th>Saldo</th><th>Consumido ({billing.days}d)</th><th>Última recarga</th><th></th></tr>
					</thead>
					<tbody>
						{#each billing.orgs as o (o.id)}
							<tr>
								<td><b>{o.name}</b></td>
								<td>
									<span class:low-balance={o.low_balance}>{fmtUsd(o.balance_usd)}</span>
									<div class="muted-sm">{fmtCrc(o.balance_usd * billing.exchange_rate_crc)}</div>
								</td>
								<td>{fmtUsd(o.consumed_usd)}</td>
								<td class="muted">{o.last_topup_at ? fmtDateTime(o.last_topup_at) : '—'}</td>
								<td class="actions">
									<button class="btn btn-secondary btn-sm" onclick={() => openTopup(o)}>
										<Icon name="plus" size={13} /> Recargar
									</button>
								</td>
							</tr>
						{/each}
					</tbody>
				</table>

				<div class="section-head"><h3>Transacciones recientes</h3></div>
				<table class="data-table">
					<thead>
						<tr><th>Cuándo</th><th>Organización</th><th>Tipo</th><th>Monto</th><th>Tokens</th><th>Modelo</th><th>Detalle</th></tr>
					</thead>
					<tbody>
						{#each billing.recent_transactions as t (t.id)}
							<tr>
								<td class="muted">{fmtDateTime(t.created_at)}</td>
								<td>{t.organization_name || `#${t.organization_id}`}</td>
								<td>
									<span class="badge {t.kind === 'topup' ? 'badge-success' : t.kind === 'consumption' ? 'badge-accent' : 'badge-warning'}">
										{t.kind === 'topup' ? 'Recarga' : t.kind === 'consumption' ? 'Consumo' : 'Ajuste'}
									</span>
								</td>
								<td class:tx-neg={t.amount_usd < 0} class:tx-pos={t.amount_usd > 0}>{fmtUsd(t.amount_usd)}</td>
								<td class="muted">{t.tokens ?? '—'}</td>
								<td class="muted-sm">{t.model_name || '—'}</td>
								<td class="muted-sm">{t.description || '—'}</td>
							</tr>
						{/each}
					</tbody>
				</table>
			{/if}

		{:else if activeTab === 'users'}
			<div class="filters">
				<div class="search-input">
					<Icon name="search" size={14} class="si" />
					<input type="text" placeholder="Buscar por nombre o email..." bind:value={userQuery} oninput={() => loadUsers()} />
				</div>
			</div>

			<table class="data-table">
				<thead>
					<tr><th>Usuario</th><th>Email</th><th>Rol</th><th>Org</th><th>Estado</th><th>Última conexión</th><th></th></tr>
				</thead>
				<tbody>
					{#each users as u}
						<tr>
							<td>
								<div class="user-cell">
									<div class="org-mark" style="background:linear-gradient(135deg, var(--accent), var(--accent-hover))">{u.name?.charAt(0)}</div>
									<div>
										<div class="org-name">{u.name}</div>
										{#if u.title}<div class="muted-sm">{u.title}</div>{/if}
									</div>
								</div>
							</td>
							<td class="muted">{u.email}</td>
							<td>
								<select class="inline-select" value={u.role} onchange={(e) => changeUserRole(u, e.target.value)}>
									<option value="super_admin">Super Admin</option>
									<option value="admin">Admin</option>
									<option value="agent">Agente</option>
									<option value="viewer">Viewer</option>
								</select>
							</td>
							<td class="muted">{u.organization?.name || '—'}</td>
							<td>
								{#if u.is_active}
									<span class="badge badge-success">Activo</span>
								{:else}
									<span class="badge">Inactivo</span>
								{/if}
							</td>
							<td class="muted">{fmtDateTime(u.last_login_at)}</td>
							<td class="actions">
								<button class="icon-btn" title="Reiniciar contraseña" onclick={() => resetUserPw(u)}>
									<Icon name="lock" size={13} />
								</button>
								<button class="icon-btn warn" title={u.is_active ? 'Desactivar' : 'Activar'} onclick={() => toggleUserActive(u)}>
									<Icon name={u.is_active ? 'x' : 'check'} size={13} />
								</button>
							</td>
						</tr>
					{/each}
				</tbody>
			</table>
			{#if !users.length}<div class="empty">Sin usuarios</div>{/if}

		{:else if activeTab === 'audit'}
			<div class="filters">
				<div class="search-input">
					<Icon name="filter" size={14} class="si" />
					<input type="text" placeholder="Filtrar por acción (ej: superadmin.org)..." bind:value={auditFilter.action} oninput={() => loadAudit(1)} />
				</div>
				<button class="btn btn-secondary btn-sm" onclick={() => loadAudit(audit.page)}>
					<Icon name="refresh" size={13} /> Refrescar
				</button>
			</div>

			<table class="data-table">
				<thead>
					<tr><th>Cuándo</th><th>Acción</th><th>Actor</th><th>Target</th><th>IP</th><th>Detalles</th></tr>
				</thead>
				<tbody>
					{#each audit.items as a}
						<tr>
							<td class="muted">{fmtDateTime(a.created_at)}</td>
							<td><code class="action-code">{a.action}</code></td>
							<td>{a.actor?.name || `#${a.actor_id || '?'}`}</td>
							<td class="muted">{a.target_type ? `${a.target_type}#${a.target_id || '?'}` : '—'}</td>
							<td class="muted-sm">{a.ip_address || '—'}</td>
							<td class="muted-sm">
								{#if Object.keys(a.metadata || {}).length}
									<details>
										<summary>ver</summary>
										<pre>{JSON.stringify(a.metadata, null, 2)}</pre>
									</details>
								{/if}
							</td>
						</tr>
					{/each}
				</tbody>
			</table>
			{#if audit.total > audit.per_page}
				<div class="pager">
					<button class="btn btn-ghost btn-sm" disabled={audit.page <= 1} onclick={() => loadAudit(audit.page - 1)}>
						<Icon name="chevron-left" size={14} /> Anterior
					</button>
					<span class="muted">Página {audit.page} · {audit.total} entradas</span>
					<button class="btn btn-ghost btn-sm" disabled={audit.page * audit.per_page >= audit.total} onclick={() => loadAudit(audit.page + 1)}>
						Siguiente <Icon name="chevron-right" size={14} />
					</button>
				</div>
			{/if}
		{/if}
	</div>
</div>
{/if}

{#if packageForm}
	<div class="modal-back" onclick={() => { editingPackage = null; packageForm = null; }} role="presentation">
		<div class="modal modal-wide" onclick={(e) => e.stopPropagation()} role="dialog">
			<div class="modal-head">
				<h3>{editingPackage?.id ? `Editar paquete: ${editingPackage.name}` : 'Nuevo paquete'}</h3>
				<button class="icon-btn" onclick={() => { editingPackage = null; packageForm = null; }}><Icon name="x" size={16} /></button>
			</div>
			<form onsubmit={submitPackage}>
				<div class="form-grid">
					<label>Nombre *
						<input type="text" bind:value={packageForm.name} required placeholder="Ej: Emprendedor" />
					</label>
					<label>Precio USD/mes
						<input type="number" min="0" step="0.01" bind:value={packageForm.price_usd} />
					</label>
					<label>Precio ₡/mes
						<input type="number" min="0" step="1" bind:value={packageForm.price_crc} />
					</label>
				</div>
				<label>Descripción
					<input type="text" bind:value={packageForm.description} placeholder="Para quién es este paquete..." />
				</label>

				<div class="divider-h">Módulos incluidos</div>
				<div class="modules-grid">
					{#each modules as m (m.key)}
						<label class="module-check" title={m.description}>
							<input type="checkbox" bind:checked={packageForm.feature_flags[m.key]} />
							<div>
								<div class="module-label">{m.label}</div>
								<div class="module-desc">{m.description}</div>
							</div>
						</label>
					{/each}
				</div>

				<div class="divider-h">Límites (-1 = ilimitado)</div>
				<div class="form-grid limits-grid">
					{#each Object.entries(LIMIT_LABELS) as [k, label]}
						<label>{label}
							<input type="number" min="-1" step="1" bind:value={packageForm[k]} />
						</label>
					{/each}
				</div>

				<label class="module-check inline">
					<input type="checkbox" bind:checked={packageForm.is_active} />
					<span>Paquete activo (asignable a organizaciones)</span>
				</label>

				<div class="modal-foot">
					<button type="button" class="btn btn-secondary" onclick={() => { editingPackage = null; packageForm = null; }}>Cancelar</button>
					<button type="submit" class="btn btn-primary">{editingPackage?.id ? 'Guardar' : 'Crear paquete'}</button>
				</div>
			</form>
		</div>
	</div>
{/if}

{#if topupOrg}
	<div class="modal-back" onclick={() => (topupOrg = null)} role="presentation">
		<div class="modal" onclick={(e) => e.stopPropagation()} role="dialog">
			<div class="modal-head">
				<h3>Recargar créditos — {topupOrg.name}</h3>
				<button class="icon-btn" onclick={() => (topupOrg = null)}><Icon name="x" size={16} /></button>
			</div>
			<form onsubmit={submitTopup}>
				<p class="muted" style="font-size: 12.5px;">
					Saldo actual: <b>{fmtUsd(topupOrg.balance_usd)}</b>
					({fmtCrc(topupOrg.balance_usd * (billing?.exchange_rate_crc || 460))})
				</p>
				<div class="form-grid" style="grid-template-columns: 1fr 1fr;">
					<label>Monto en colones
						<input type="number" min="0" step="100" bind:value={topupForm.amount_crc}
							oninput={syncTopupFromCrc} placeholder="₡" />
					</label>
					<label>Monto en USD *
						<input type="number" min="0.01" step="0.01" bind:value={topupForm.amount_usd}
							oninput={syncTopupFromUsd} required placeholder="$" />
					</label>
				</div>
				<label>Descripción / referencia de pago
					<input type="text" bind:value={topupForm.description} placeholder="Ej: SINPE #123456" />
				</label>
				<div class="modal-foot">
					<button type="button" class="btn btn-secondary" onclick={() => (topupOrg = null)}>Cancelar</button>
					<button type="submit" class="btn btn-primary">Acreditar</button>
				</div>
			</form>
		</div>
	</div>
{/if}

{#if providerForm}
	<div class="modal-back" onclick={() => { editingProvider = null; providerForm = null; }} role="presentation">
		<div class="modal" onclick={(e) => e.stopPropagation()} role="dialog">
			<div class="modal-head">
				<h3>{editingProvider?.id ? `Editar: ${editingProvider.name}` : 'Nuevo proveedor de plataforma'}</h3>
				<button class="icon-btn" onclick={() => { editingProvider = null; providerForm = null; }}><Icon name="x" size={16} /></button>
			</div>
			<form onsubmit={submitProvider}>
				<label>Nombre visible para clientes *
					<input type="text" bind:value={providerForm.name} required placeholder="Mangu AI" />
				</label>
				<label>Proveedor
					<select bind:value={providerForm.provider_type}>
						{#each Object.entries(modelsCatalog) as [key, info]}
							<option value={key}>{info.label || key}</option>
						{/each}
					</select>
				</label>
				<label>Modelo
					<select bind:value={providerForm.model_name}>
						<option value="">— Selecciona un modelo —</option>
						{#each (modelsCatalog[providerForm.provider_type]?.models || []) as m}
							<option value={m.id}>{m.name} (${m.input}/{m.output} por 1M)</option>
						{/each}
					</select>
				</label>
				<label>API key {editingProvider?.id ? '(dejar vacío para no cambiarla)' : '*'}
					<input type="password" bind:value={providerForm.api_key}
						required={!editingProvider?.id} placeholder="sk-..." autocomplete="off" />
				</label>
				<label class="module-check inline">
					<input type="checkbox" bind:checked={providerForm.is_active} />
					<span>Activo (visible para las organizaciones)</span>
				</label>
				<div class="modal-foot">
					<button type="button" class="btn btn-secondary" onclick={() => { editingProvider = null; providerForm = null; }}>Cancelar</button>
					<button type="submit" class="btn btn-primary">{editingProvider?.id ? 'Guardar' : 'Crear'}</button>
				</div>
			</form>
		</div>
	</div>
{/if}

{#if creatingOrg}
	<div class="modal-back" onclick={() => (creatingOrg = false)} role="presentation">
		<div class="modal" onclick={(e) => e.stopPropagation()} role="dialog">
			<div class="modal-head">
				<h3>Nueva organización</h3>
				<button class="icon-btn" onclick={() => (creatingOrg = false)}><Icon name="x" size={16} /></button>
			</div>
			<form onsubmit={submitCreate}>
				<label>Nombre *
					<input type="text" bind:value={createForm.name} required />
				</label>
				<label>Plan
					<select bind:value={createForm.plan}>
						<option value="free">Free</option>
						<option value="starter">Starter</option>
						<option value="pro">Pro</option>
						<option value="enterprise">Enterprise</option>
					</select>
				</label>
				<div class="divider-h">Admin inicial (opcional)</div>
				<label>Email del admin
					<input type="email" bind:value={createForm.admin_email} placeholder="admin@empresa.com" />
				</label>
				<label>Nombre del admin
					<input type="text" bind:value={createForm.admin_name} />
				</label>
				<label>Contraseña inicial
					<input type="text" bind:value={createForm.admin_password} placeholder="Mín 8 chars, letra y número" />
				</label>
				<div class="modal-foot">
					<button type="button" class="btn btn-secondary" onclick={() => (creatingOrg = false)}>Cancelar</button>
					<button type="submit" class="btn btn-primary">Crear</button>
				</div>
			</form>
		</div>
	</div>
{/if}

<style>
	.sa-view { height: 100%; display: flex; flex-direction: column; background: var(--surface-1); }
	.sa-tabs {
		display: flex; gap: 4px;
		padding: 12px 24px 0;
		background: var(--surface-0);
		border-bottom: 1px solid var(--border-1);
		flex-shrink: 0;
	}
	.sa-tabs button {
		display: inline-flex; align-items: center; gap: 6px;
		padding: 10px 16px;
		font-size: 13px; font-weight: 600;
		color: var(--text-3);
		border-bottom: 2px solid transparent;
		margin-bottom: -1px;
		transition: color var(--transition-fast);
	}
	.sa-tabs button:hover { color: var(--text-1); }
	.sa-tabs button.active { color: var(--accent); border-bottom-color: var(--accent); }

	.sa-body { flex: 1; overflow-y: auto; padding: 20px 24px 24px; }
	.loading { display: flex; align-items: center; justify-content: center; padding: 60px; }
	.spinner { width: 32px; height: 32px; border: 3px solid var(--border-1); border-top-color: var(--accent); border-radius: 50%; animation: spin 700ms linear infinite; }
	@keyframes spin { to { transform: rotate(360deg); } }
	.empty { text-align: center; padding: 60px; color: var(--text-3); font-size: 14px; }

	.kpis {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
		gap: 12px;
		margin-bottom: 18px;
	}
	.kpi { padding: 16px; }
	.kpi-num { font-size: 28px; font-weight: 800; color: var(--text-1); letter-spacing: -0.02em; line-height: 1.1; }
	.kpi-lbl { font-size: 12px; color: var(--text-2); margin-top: 2px; font-weight: 600; }
	.kpi-sub { font-size: 11px; color: var(--brand-dark); margin-top: 4px; }
	.kpi-sub.muted { color: var(--text-3); }

	.charts {
		display: grid;
		grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
		gap: 14px;
	}
	.chart-card, .plans-card { padding: 18px; }
	.chart-card h3, .plans-card h3 { font-size: 14px; margin-bottom: 14px; }
	.chart { width: 100%; max-height: 200px; }
	.legend { display: flex; gap: 16px; font-size: 11.5px; color: var(--text-3); margin-top: 8px; }
	.dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 4px; }

	.plan-dist { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
	.plan-dist li { display: grid; grid-template-columns: auto auto 1fr; gap: 8px; align-items: center; }
	.dist-count { font-weight: 700; font-size: 13px; }
	.dist-bar { height: 6px; background: var(--surface-2); border-radius: 3px; overflow: hidden; }
	.dist-fill { height: 100%; background: var(--accent); border-radius: 3px; }

	.filters {
		display: flex; gap: 8px; align-items: center;
		margin-bottom: 14px; flex-wrap: wrap;
	}
	.search-input { position: relative; flex: 1; min-width: 240px; max-width: 360px; }
	.search-input :global(.si) { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-3); }
	.search-input input { padding-left: 32px; height: 32px; font-size: 13px; }
	.filters select { width: auto; height: 32px; font-size: 13px !important; padding: 4px 28px 4px 10px !important; }

	.data-table {
		width: 100%;
		border-collapse: collapse;
		background: var(--surface-0);
		border-radius: 10px;
		overflow: hidden;
		border: 1px solid var(--border-1);
		font-size: 13px;
	}
	.data-table th {
		background: var(--surface-1);
		text-align: left;
		padding: 10px 14px;
		font-size: 11.5px;
		font-weight: 700;
		color: var(--text-3);
		text-transform: uppercase;
		letter-spacing: 0.04em;
		border-bottom: 1px solid var(--border-1);
	}
	.data-table td {
		padding: 12px 14px;
		border-bottom: 1px solid var(--border-1);
		vertical-align: middle;
	}
	.data-table tr:last-child td { border-bottom: none; }
	.data-table tr:hover td { background: var(--surface-1); }
	.muted { color: var(--text-3); }
	.muted-sm { color: var(--text-3); font-size: 11.5px; }

	.org-link {
		display: flex; align-items: center; gap: 10px;
		text-align: left;
	}
	.org-mark {
		width: 32px; height: 32px;
		border-radius: 8px;
		display: flex; align-items: center; justify-content: center;
		color: #fff; font-weight: 700;
	}
	.org-name { font-weight: 600; color: var(--text-1); }
	.org-slug { font-size: 11px; color: var(--text-3); font-family: var(--font-mono); }
	.user-cell { display: flex; align-items: center; gap: 10px; }
	.actions { display: flex; gap: 4px; justify-content: flex-end; }
	.icon-btn {
		width: 28px; height: 28px;
		display: flex; align-items: center; justify-content: center;
		border-radius: 6px;
		color: var(--text-3);
		transition: background var(--transition-fast);
	}
	.icon-btn:hover { background: var(--surface-2); color: var(--text-1); }
	.icon-btn.warn:hover { background: var(--warning-soft); color: #b45309; }
	.icon-btn.danger:hover { background: var(--danger-soft); color: var(--danger); }
	.icon-btn.success:hover { background: var(--success-soft); color: var(--success); }

	.inline-select {
		width: auto !important;
		font-size: 12px !important;
		padding: 3px 22px 3px 8px !important;
		font-weight: 500;
	}

	.action-code {
		font-family: var(--font-mono);
		font-size: 11.5px;
		background: var(--surface-2);
		padding: 2px 6px;
		border-radius: 4px;
		color: var(--text-2);
	}

	.pager {
		display: flex; align-items: center; justify-content: space-between;
		margin-top: 14px;
		font-size: 12.5px;
	}

	/* Billing */
	.danger-num { color: var(--danger); }
	.low-balance { color: var(--danger); font-weight: 700; }
	.tx-neg { color: var(--text-2); }
	.tx-pos { color: var(--success); font-weight: 600; }
	.chart-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
	.chart-head h3 { margin-bottom: 0; }
	.range-mini { display: flex; gap: 4px; }
	.range-mini button {
		padding: 3px 10px; font-size: 11.5px; font-weight: 600;
		border-radius: 6px; color: var(--text-3);
	}
	.range-mini button:hover { background: var(--surface-2); }
	.range-mini button.active { background: var(--accent-soft); color: var(--accent); }
	.section-head {
		display: flex; align-items: center; justify-content: space-between;
		margin: 18px 0 10px;
	}
	.section-head h3 { font-size: 14px; }
	.settings-form { display: flex; flex-direction: column; gap: 10px; }
	.settings-form label { font-size: 12px; }

	/* Packages */
	.pkg-inactive td { opacity: 0.55; }
	.mod-chips { display: flex; flex-wrap: wrap; gap: 4px; max-width: 280px; }
	.form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
	.limits-grid { grid-template-columns: repeat(3, 1fr); }
	.modules-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
	.module-check {
		display: flex; gap: 10px; align-items: flex-start;
		padding: 10px 12px;
		border: 1px solid var(--border-1);
		border-radius: 8px;
		cursor: pointer;
		transition: border-color var(--transition-fast), background var(--transition-fast);
		font-size: 12.5px;
	}
	.module-check:hover { border-color: var(--accent); background: var(--accent-soft); }
	.module-check input { width: auto; margin-top: 2px; }
	.module-check.inline { align-items: center; }
	.module-label { font-weight: 600; color: var(--text-1); }
	.module-desc { font-size: 11px; color: var(--text-3); line-height: 1.35; }

	/* Modal */
	.modal-back { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; }
	.modal { background: var(--surface-0); border-radius: 14px; box-shadow: var(--shadow-xl); width: 100%; max-width: 480px; max-height: 90vh; overflow-y: auto; }
	.modal-wide { max-width: 640px; }
	.modal-head { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid var(--border-1); }
	.modal-head h3 { font-size: 16px; }
	form { padding: 16px 20px; display: flex; flex-direction: column; gap: 12px; }
	form label { font-size: 12.5px; }
	.divider-h {
		font-size: 11px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: var(--text-3);
		padding: 8px 0 4px;
		border-top: 1px solid var(--border-1);
		margin-top: 4px;
	}
	.modal-foot { display: flex; gap: 8px; justify-content: flex-end; padding-top: 12px; border-top: 1px solid var(--border-1); }

	pre {
		font-size: 10.5px;
		font-family: var(--font-mono);
		background: var(--surface-2);
		padding: 6px 8px;
		border-radius: 4px;
		max-width: 320px;
		overflow-x: auto;
		margin: 4px 0 0;
	}
</style>
